/**
 * WaveformToolbar Component
 * Floating toolbar with editing tools for waveform manipulation
 */
import { motion } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Scissors,
  Copy,
  Clipboard,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Repeat,
  Play,
  Pause,
  Square,
  Trash2,
} from 'lucide-react';

export const WaveformToolbar = ({
  hasSelection = false,
  hasClipboard = false,
  canUndo = false,
  canRedo = false,
  isPlaying = false,
  loopEnabled = false,
  onCut,
  onCopy,
  onPaste,
  onDelete,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onToggleLoop,
  onPlay,
  onPause,
  onStop,
  className = '',
}) => {
  const tools = [
    {
      icon: isPlaying ? Pause : Play,
      label: isPlaying ? 'Pause' : 'Play',
      onClick: isPlaying ? onPause : onPlay,
      shortcut: 'Space',
      testId: 'waveform-play-btn',
    },
    {
      icon: Square,
      label: 'Stop',
      onClick: onStop,
      testId: 'waveform-stop-btn',
    },
    { type: 'divider' },
    {
      icon: Scissors,
      label: 'Cut',
      onClick: onCut,
      disabled: !hasSelection,
      shortcut: '⌘X',
      testId: 'waveform-cut-btn',
    },
    {
      icon: Copy,
      label: 'Copy',
      onClick: onCopy,
      disabled: !hasSelection,
      shortcut: '⌘C',
      testId: 'waveform-copy-btn',
    },
    {
      icon: Clipboard,
      label: 'Paste',
      onClick: onPaste,
      disabled: !hasClipboard,
      shortcut: '⌘V',
      testId: 'waveform-paste-btn',
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: onDelete,
      disabled: !hasSelection,
      shortcut: 'Del',
      variant: 'destructive',
      testId: 'waveform-delete-btn',
    },
    { type: 'divider' },
    {
      icon: Undo2,
      label: 'Undo',
      onClick: onUndo,
      disabled: !canUndo,
      shortcut: '⌘Z',
      testId: 'waveform-undo-btn',
    },
    {
      icon: Redo2,
      label: 'Redo',
      onClick: onRedo,
      disabled: !canRedo,
      shortcut: '⌘⇧Z',
      testId: 'waveform-redo-btn',
    },
    { type: 'divider' },
    {
      icon: ZoomIn,
      label: 'Zoom In',
      onClick: onZoomIn,
      shortcut: '+',
      testId: 'waveform-zoom-in-btn',
    },
    {
      icon: ZoomOut,
      label: 'Zoom Out',
      onClick: onZoomOut,
      shortcut: '-',
      testId: 'waveform-zoom-out-btn',
    },
    { type: 'divider' },
    {
      icon: Repeat,
      label: loopEnabled ? 'Disable Loop' : 'Enable Loop',
      onClick: onToggleLoop,
      active: loopEnabled,
      shortcut: 'L',
      testId: 'waveform-loop-btn',
    },
  ];

  return (
    <motion.div
      className={`
        fixed bottom-8 left-1/2 -translate-x-1/2 z-40
        px-4 py-2.5
        bg-zinc-900/90 backdrop-blur-xl
        border border-white/10
        rounded-full
        shadow-2xl
        flex items-center gap-1
        ${className}
      `}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: 'spring', damping: 20 }}
      data-testid="waveform-toolbar"
    >
      {tools.map((tool, index) => {
        if (tool.type === 'divider') {
          return (
            <div 
              key={`divider-${index}`} 
              className="w-px h-6 bg-zinc-700 mx-1"
            />
          );
        }

        const Icon = tool.icon;
        
        return (
          <Button
            key={tool.label}
            variant={tool.active ? 'default' : tool.variant || 'ghost'}
            size="icon"
            disabled={tool.disabled}
            onClick={() => {
              tool.onClick?.();
            }}
            className={`
              w-9 h-9 rounded-full
              ${tool.active ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : ''}
              ${tool.disabled ? 'opacity-40' : ''}
              transition-all
            `}
            title={`${tool.label}${tool.shortcut ? ` (${tool.shortcut})` : ''}`}
            data-testid={tool.testId}
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        );
      })}
    </motion.div>
  );
};

export default WaveformToolbar;
